<template>
    <div class="relative min-h-screen bg-gradient-to-br from-gray-100 to-blue-50">

        <!-- Toggle Filter Button (top-left corner) -->
        <button @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter"
                class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Entities</label>
                        <multiselect v-model="filters.entities" :options="entityOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select entities" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Campuses</label>
                        <multiselect v-model="filters.campuses" :options="campusOptionsFiltered" :multiple="true"
                            :close-on-select="false" placeholder="Select campuses" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">User Types</label>
                        <multiselect v-model="filters.userTypes" :options="userTypeOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select user types" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Departments</label>
                        <multiselect v-model="filters.departments" :options="departmentOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select departments" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Person</label>
                        <multiselect v-model="filters.person" :options="personOptions" :multiple="false"
                            :close-on-select="true" placeholder="Select person" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Engagement Type</label>
                        <multiselect v-model="filters.engagementTypes" :options="engagementTypeOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select engagement types" track-by="id" label="name">
                        </multiselect>
                    </div>
                </div>

                <!-- Apply Button -->
                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <div class="h-full container mx-auto">
            <div class="flex flex-col h-full w-full p-8">
                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <div v-if="role == 'admin' || role == 'superadmin' || role == 'Viewer'" class="flex">
                        <router-link to="/academic-industry-engagement-hub-management"
                            class="hover:text-blue-700 transition font-medium">Academic Industry Engagement
                            Hub</router-link>
                        <span class="mx-1">/</span>
                    </div>
                    <span class="text-blue-900 font-semibold">Academic Industry Engagement Hub</span>
                </nav>

                <!-- Page Header -->
                <div class="bg-white border border-gray-200 rounded-3xl shadow-md p-8 mb-10">
                    <h1 class="text-3xl font-bold text-blue-900 flex items-center gap-3">
                        🤝 Academic Industry Engagement Hub
                    </h1>
                    <p class="text-gray-600 mt-2 text-base leading-relaxed max-w-3xl">
                        A dynamic space designed to strengthen partnerships between academia and industry.
                        Faculty and students can collaborate, innovate, and engage with real-world opportunities
                        to drive educational and professional growth.
                    </p>
                </div>

                <!-- Dashboard Cards -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    <!-- Total Engagements Card -->
                    <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-blue-100 text-sm font-medium">Total Engagements</p>
                                <p class="text-3xl font-bold mt-2">{{
                                    formatNumber(engagementHubStatistics.totalEngagements) }}</p>
                                <p class="text-blue-100 text-xs mt-1">Submitted Proposals</p>
                            </div>
                            <div class="bg-blue-400 bg-opacity-30 rounded-full p-3">
                                <font-awesome-icon :icon="['fas', 'handshake']" class="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <!-- Funded Engagements Card -->
                    <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-green-100 text-sm font-medium">Funded Engagements</p>
                                <p class="text-3xl font-bold mt-2">{{
                                    formatNumber(engagementHubStatistics.fundedEngagements) }}</p>
                                <p class="text-green-100 text-xs mt-1">{{ getFundedPercentage() }}% of total</p>
                            </div>
                            <div class="bg-green-400 bg-opacity-30 rounded-full p-3">
                                <img class="h-6 w-6" src="../../assets/images/default/DirhamWhite.svg" alt="AED" />
                            </div>
                        </div>
                    </div>

                    <!-- Top Engagement Type Card -->
                    <div class="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-purple-100 text-sm font-medium">Top Engagement Type</p>
                                <p class="text-2xl font-bold mt-2">{{ getTopEngagementType() }}</p>
                                <p class="text-purple-100 text-xs mt-1">{{ getTopEngagementTypeCount() }} engagements
                                </p>
                            </div>
                            <div class="bg-purple-400 bg-opacity-30 rounded-full p-3">
                                <font-awesome-icon :icon="['fas', 'chart-bar']" class="text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Charts Section -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <!-- Engagements by Type Chart -->
                    <EngagementsByTypeChart :data="engagementHubStatistics.engagementsByType"
                        @type-clicked="onTypeChartClick" />

                    <!-- Engagements by Entity Chart -->
                    <EngagementsByEntityChart :data="engagementHubStatistics.engagementsByEntity"
                        @entity-clicked="onEntityChartClick" />
                </div>

                <!-- Advanced Search (after charts) -->
                <div class="container mx-auto px-0 mb-4">
                    <AIEHAdvancedSearch :initial-basic-search="basicSearchText"
                        :initial-advanced-search="advancedSearch" @on-basic-search="onBasicSearch"
                        @on-advanced-search="onAdvancedSearch" @on-clear-search="clearSearch" />
                </div>

                <!-- Ideas Section -->
                <section class="bg-white rounded-3xl shadow-md p-8 border border-gray-200 flex-auto flex flex-col">
                    <div class="flex justify-between items-center mb-6">
                        <h2 class="text-2xl font-semibold text-blue-900">🎯 Submitted Engagement Ideas</h2>
                        <button @click="showForm = true"
                            class="flex items-center px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full shadow-lg hover:from-green-600 hover:to-green-700 transition">
                            <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                            Share Engagement
                        </button>
                    </div>

                    <div v-if="isLoading" class="grid md:grid-cols-2 gap-6">
                        <div v-for="n in first" :key="n"
                            class="bg-white border border-gray-200 p-6 rounded-2xl shadow animate-pulse">
                            <div class="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
                            <div class="space-y-2">
                                <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                                <div class="h-3 bg-gray-200 rounded w-2/3"></div>
                                <div class="h-3 bg-gray-200 rounded w-1/3"></div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="!isLoading && !hasItems"
                        class="flex-auto flex flex-col justify-center items-center py-16 px-6">
                        <div class="text-center max-w-md">
                            <!-- Icon -->
                            <div class="mb-6">
                                <div
                                    class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                                    <font-awesome-icon :icon="['fas', 'lightbulb']" class="h-12 w-12 text-gray-400" />
                                </div>
                            </div>

                            <!-- Title -->
                            <h2 class="text-2xl font-semibold text-gray-800 mb-3">No Engagements Found</h2>

                            <!-- Description -->
                            <p class="text-gray-500 mb-6 leading-relaxed">
                                We couldn't find any engagements matching your current search criteria.
                                Try adjusting your filters or search terms to find what you're looking for.
                            </p>

                            <!-- Action Buttons -->
                            <div class="flex flex-col sm:flex-row gap-3 justify-center">
                                <button @click="clearFilters()"
                                    class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium">
                                    <font-awesome-icon :icon="['fas', 'filter']" class="h-4 w-4 mr-2" />
                                    Clear All Filters
                                </button>
                                <button @click="clearSearch()"
                                    class="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium">
                                    <font-awesome-icon :icon="['fas', 'search']" class="h-4 w-4 mr-2" />
                                    Clear Search
                                </button>
                            </div>

                            <!-- Help Text -->
                            <div class="mt-8 pt-6 border-t border-gray-200">
                                <p class="text-sm text-gray-400">
                                    <font-awesome-icon :icon="['fas', 'lightbulb']" class="h-4 w-4 mr-1" />
                                    Tip: Use the advanced search to find engagements by type, partner, or date.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div v-else class="flex flex-col">
                        <div class="grid md:grid-cols-2 gap-6 mt-6">
                            <div v-for="(item, index) in items" :key="index" @click.stop="viewItem(item)"
                                class="relative bg-white border border-gray-200 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-blue-400">

                                <!-- 👤 User Info Section -->
                                <div @click.stop="goToProfile(item.user.token)"
                                    :title="`Click to view ${item.user.name}'s profile`"
                                    class="flex items-start justify-between gap-4 mb-4 group">
                                    <div class="flex items-center gap-3">
                                        <img :src="profilePicture(item.user.profile_picture_url)" alt="User Avatar"
                                            class="w-8 sm:w-10 h-8 sm:h-10 rounded-full object-cover border" />
                                        <div>
                                            <h4
                                                class="text-sm sm:text-base font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                                                {{ item.user.name }}
                                            </h4>
                                            <p
                                                class="text-xs text-gray-400 group-hover:text-blue-600 transition-colors">
                                                {{ postedAt(item.created_at) }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <!-- Edit Button -->
                                        <button v-if="canEditItem(item)" @click.stop="editItem(item)"
                                            class="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                            title="Edit this engagement">
                                            <font-awesome-icon :icon="['fas', 'edit']" class="w-4 h-4" />
                                        </button>
                                        <p class="text-xs text-gray-400">
                                            #{{ paginatorInfo.firstItem + index }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Title -->
                                <div class="flex items-center justify-between mb-2">
                                    <h3 class="text-xl font-bold text-blue-900 truncate">
                                        {{ item.title || 'Untitled Engagement' }}
                                    </h3>
                                </div>

                                <!-- Description -->
                                <div class="text-gray-700 text-sm mb-3 line-clamp-3 truncate-3">
                                    {{ item.description || 'No description provided.' }}
                                </div>

                                <!-- Student-specific details -->
                                <div v-if="item.user.role_name === 'user'" class="text-sm text-gray-600 space-y-1 mb-3">
                                    <div><strong>Expected Outcome:</strong> {{ item.expected_outcome || '—' }}</div>
                                    <div><strong>Mentor:</strong> {{ item.mentor || '—' }}</div>
                                    <div><strong>Company / Industry Partner:</strong> {{ item.industry_partner || '—' }}
                                    </div>
                                    <!-- KPI Information -->
                                    <div v-if="item.kpiPillar" class="mt-2 pt-2 border-t border-gray-200">
                                        <div><strong>KPI Pillar:</strong> {{ item.kpiPillar.id }}. {{
                                            item.kpiPillar.title }} ({{
                                                item.kpiPillar.weight }}%)</div>
                                        <div v-if="item.kpiSubSection"><strong>KPI Sub-Section:</strong> {{
                                            item.kpiSubSection.kpi_code }} {{ item.kpiSubSection.title }}</div>
                                    </div>
                                </div>
                                <!-- Faculty-specific details -->
                                <div v-else class="text-sm text-gray-600 space-y-1 mb-3">
                                    <div><strong>Engagement Type:</strong> {{ item.engagement_type || '—' }}</div>
                                    <div><strong>Date of Engagement:</strong> {{ item.date_of_engagement ?
                                        getFormattedDate(item.date_of_engagement) : '—' }}</div>
                                    <div><strong>Target Outcome:</strong> {{ item.target_outcome || '—' }}</div>
                                    <div><strong>External Organization:</strong> {{ item.industry_partner || '—' }}
                                    </div>
                                    <div>
                                        <strong>Funded Initiative:</strong>
                                        <span v-if="item.funded">Yes</span>
                                        <span v-else>No</span>
                                    </div>
                                    <div v-if="item.funded && item.funding_details">
                                        <strong>Funding Details:</strong> {{ item.funding_details }}
                                    </div>
                                    <!-- KPI Information -->
                                    <div v-if="item.kpiPillar" class="mt-2 pt-2 border-t border-gray-200">
                                        <div><strong>KPI Pillar:</strong> {{ item.kpiPillar.id }}. {{
                                            item.kpiPillar.title }} ({{
                                                item.kpiPillar.weight }}%)</div>
                                        <div v-if="item.kpiSubSection"><strong>KPI Sub-Section:</strong> {{
                                            item.kpiSubSection.kpi_code }} {{ item.kpiSubSection.title }}</div>
                                    </div>
                                </div>

                                <!-- Attachments -->
                                <div v-if="item.attachments && item.attachments.length" class="mt-4">
                                    <h4 class="text-sm font-semibold text-gray-700 mb-2">📎 Attached Files:</h4>
                                    <ul class="space-y-1">
                                        <li v-for="(file, idx) in item.attachments" :key="idx"
                                            class="flex items-center space-x-2 text-sm">
                                            <a @click.stop :href="getUrl(file.file_path)" target="_blank"
                                                class="text-blue-600 hover:underline flex items-center gap-2">
                                                <font-awesome-icon :icon="['fas', 'file-alt']" class="text-gray-500" />
                                                <span>{{ file.file_name || 'Document' }}</span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>

                                <!-- No attachments -->
                                <div v-else class="text-sm text-gray-400 italic mt-4">
                                    No files uploaded.
                                </div>

                                <div class="absolute bottom-4 right-4">
                                    <img :src="entityLogo(item.user.entity)" :alt="item.user.entity.split(',')[0]"
                                        class="w-8 h-8 sm:w-10 sm:h-10 object-contain rounded border bg-white shadow" />
                                </div>

                            </div>
                        </div>
                        <div class="mt-4">
                            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage">
                            </pagination>
                        </div>
                    </div>
                </section>

                <!-- Modal for Add Idea -->
                <transition name="fade">
                    <div v-if="showForm"
                        class="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-start justify-center pt-24 px-4">

                        <div
                            class="bg-white w-full max-w-2xl rounded-3xl shadow-2xl p-6 sm:p-8 relative border border-gray-300 flex flex-col">

                            <!-- Close Button -->
                            <button class="absolute top-5 right-5 text-gray-500 hover:text-red-600 text-xl"
                                @click="showForm = false">
                                <font-awesome-icon :icon="['fas', 'times']" />
                            </button>

                            <!-- Title (fixed / non-scrollable) -->
                            <h2 class="text-2xl font-bold text-blue-900 mb-4 sticky top-0 z-10 bg-white pt-2">Submit an
                                Engagement Proposal</h2>

                            <!-- Scrollable Form Body -->
                            <form @submit.prevent="submitForm" class="">

                                <div class="overflow-y-auto pr-2 space-y-3" style="max-height: 60vh;">

                                    <!-- Title Field -->
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                        <input v-model="form.title" type="text"
                                            class="w-full p-3 border border-gray-300 rounded-xl focus:outline-none"
                                            placeholder="Title" required />
                                    </div>

                                    <!-- Description -->
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <textarea v-model="form.description" rows="4"
                                            class="w-full p-3 border border-gray-300 rounded-xl focus:outline-none"
                                            placeholder="Describe your engagement idea" required></textarea>
                                    </div>

                                    <!-- Student-specific fields -->
                                    <div v-if="role === 'user'" class="space-y-4">
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Expected
                                                Outcome</label>
                                            <input v-model="form.expectedOutcome" type="text"
                                                class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                                placeholder="e.g. Internship, mentorship, placement" />
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Institutional
                                                Mentor</label>
                                            <input v-model="form.mentor" type="text"
                                                class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                                placeholder="Faculty name (if applicable)" />
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Company /
                                                Industry
                                                Partner</label>
                                            <multiselect v-model="form.industryPartnerId" :options="currentPartners"
                                                track-by="id" :custom-label="customLabelExternalOrganization"
                                                placeholder="Optional (if applicable)">
                                            </multiselect>
                                            <input v-if="showOtherField" v-model="form.industryPartner" type="text"
                                                class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                                placeholder="Enter External Organization" />
                                        </div>

                                        <!-- KPI Pillar Selection -->
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">KPI Pillar <span
                                                    class="text-red-500">*</span></label>
                                            <multiselect v-model="form.kpiPillarId" :options="formattedKpiPillars"
                                                track-by="id" label="formattedTitle" placeholder="Select KPI Pillar"
                                                @select="onKpiPillarSelect" required>
                                            </multiselect>
                                        </div>

                                        <!-- KPI Sub-Section Selection -->
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">KPI Sub-Section
                                                <span class="text-red-500">*</span></label>
                                            <multiselect v-model="form.kpiSubSectionId"
                                                :options="formattedKpiSubSections" track-by="id" label="formattedTitle"
                                                placeholder="Select KPI Sub-Section" :disabled="!form.kpiPillarId"
                                                required>
                                            </multiselect>
                                        </div>
                                    </div>

                                    <!-- Faculty-specific fields -->
                                    <div v-else class="space-y-4">
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Type of
                                                Engagement</label>
                                            <select v-model="form.engagementType"
                                                class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none">
                                                <option disabled value="">Select engagement type</option>
                                                <option>Curriculum Development</option>
                                                <option>Research</option>
                                                <option>Guest Lecture</option>
                                                <option>Industry Visit</option>
                                                <option>Project Collaboration</option>
                                                <option>Workshop</option>
                                                <option>Seminar</option>
                                                <option>Panel Discussion</option>
                                                <option>Patent</option>
                                                <option>Other</option>
                                            </select>
                                            <input v-if="form.engagementType == 'Other'" v-model="otherEngagementType"
                                                type="text"
                                                class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                                placeholder="Other Type of Engagement" />
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Date of
                                                Engagement</label>
                                            <date-picker v-model:value="form.dateOfEngagement" format="YYYY-MM-DD"
                                                type="date" value-type="format"
                                                placeholder="Date of Engagement"></date-picker>
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Target
                                                Outcome</label>
                                            <input v-model="form.targetOutcome" type="text"
                                                class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                                placeholder="e.g. Published paper, internship program" />
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">External
                                                Organization</label>
                                            <multiselect v-model="form.industryPartnerId" :options="currentPartners"
                                                track-by="id" :custom-label="customLabelExternalOrganization"
                                                placeholder="Optional (if applicable)">
                                            </multiselect>
                                            <input v-if="showOtherField" v-model="form.industryPartner" type="text"
                                                class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                                placeholder="Enter External Organization" />
                                        </div>

                                        <!-- KPI Pillar Selection -->
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">KPI Pillar <span
                                                    class="text-red-500">*</span></label>
                                            <multiselect v-model="form.kpiPillarId" :options="formattedKpiPillars"
                                                track-by="id" label="formattedTitle" placeholder="Select KPI Pillar"
                                                @select="onKpiPillarSelect" required>
                                            </multiselect>
                                        </div>

                                        <!-- KPI Sub-Section Selection -->
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">KPI Sub-Section
                                                <span class="text-red-500">*</span></label>
                                            <multiselect v-model="form.kpiSubSectionId"
                                                :options="formattedKpiSubSections" track-by="id" label="formattedTitle"
                                                placeholder="Select KPI Sub-Section" :disabled="!form.kpiPillarId"
                                                required>
                                            </multiselect>
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Is this part
                                                of
                                                a
                                                funded initiative?</label>
                                            <div class="flex gap-6 mt-2">
                                                <label class="inline-flex items-center">
                                                    <input type="radio" v-model="form.funded" :value="true"
                                                        class="mr-2" />
                                                    Yes
                                                </label>
                                                <label class="inline-flex items-center">
                                                    <input type="radio" v-model="form.funded" :value="false"
                                                        class="mr-2" />
                                                    No
                                                </label>
                                            </div>
                                        </div>

                                        <div v-if="form.funded">
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Funding
                                                Entity</label>
                                            <input v-model="form.fundingDetails" type="text"
                                                class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                                placeholder="e.g. ADEK Grant 2025" />
                                        </div>
                                    </div>

                                    <!-- Has Documents -->
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">Do you have any
                                            documents to upload?</label>
                                        <div class="flex gap-6 mt-2">
                                            <label class="inline-flex items-center">
                                                <input type="radio" v-model="form.hasDocuments" :value="true"
                                                    class="mr-2" />
                                                Yes
                                            </label>
                                            <label class="inline-flex items-center">
                                                <input type="radio" v-model="form.hasDocuments" :value="false"
                                                    class="mr-2" />
                                                No
                                            </label>
                                        </div>
                                    </div>

                                    <!-- Multiple File Upload -->
                                    <div v-if="form.hasDocuments" class="space-y-4">
                                        <div v-for="(doc, index) in form.documents" :key="index"
                                            class="flex items-center gap-3">
                                            <input v-model="doc.label" type="text" placeholder="Document Label"
                                                class="w-1/2 p-2 border border-gray-300 rounded-xl" />
                                            <input type="file" @change="handleFileUpload($event, index)"
                                                class="w-1/2 p-2 border border-gray-300 rounded-xl" />
                                            <button type="button" @click="removeDocument(index)"
                                                class="text-red-500 hover:text-red-700 ml-2">Remove</button>
                                        </div>
                                        <button type="button" @click="addDocument"
                                            class="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                            <font-awesome-icon :icon="['fas', 'plus']" />
                                            Add Document
                                        </button>
                                    </div>

                                </div>

                                <!-- Form Actions (sticky footer) -->
                                <div class="flex justify-end gap-3 mt-6">
                                    <button type="button" @click="cancelSubmit"
                                        class="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition">
                                        Cancel
                                    </button>
                                    <button type="submit"
                                        class="px-5 py-2.5 bg-blue-600 text-white rounded-full shadow hover:bg-blue-700 transition">
                                        Submit
                                    </button>
                                </div>

                            </form>

                        </div>
                    </div>
                </transition>

            </div>
        </div>



        <transition name="fade" mode="out-in">
            <base-full-modal v-if="isViewItem" :title="`View Academic Industry Engagement Hub`" height=""
                @close="closeModal">
                <think-tank-view class="text-left" :item="selectedItem" @close="closeModal"></think-tank-view>
            </base-full-modal>
        </transition>

        <!-- Edit Modal -->

        <base-modal v-if="showEditModal" title="Edit Engagement Idea" height="80vh" @close="closeEditModal">
            <form class="h-full flex flex-col" @submit.prevent="updateEngagement">
                <div class="flex-auto flex flex-col overflow-y-auto px-8 pt-6 pb-8">
                    <div class="space-y-4">
                        <!-- Title Field -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Title <span
                                    class="text-red-500">*</span></label>
                            <input v-model="editForm.title" type="text" required
                                class="w-full p-3 border border-gray-300 rounded-xl focus:outline-none"
                                placeholder="Title" />
                        </div>

                        <!-- Description -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Description <span
                                    class="text-red-500">*</span></label>
                            <textarea v-model="editForm.description" required rows="4"
                                class="w-full p-3 border border-gray-300 rounded-xl focus:outline-none"
                                placeholder="Describe your engagement idea"></textarea>
                        </div>

                        <!-- Student-specific fields -->
                        <div v-if="editingItem.user.role_name === 'user'" class="space-y-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Expected Outcome</label>
                                <input v-model="editForm.expectedOutcome" type="text"
                                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                    placeholder="e.g. Internship, mentorship, placement" />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Institutional Mentor</label>
                                <input v-model="editForm.mentor" type="text"
                                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                    placeholder="Faculty name (if applicable)" />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Company / Industry
                                    Partner</label>
                                <multiselect v-model="editForm.industryPartnerId" :options="currentPartners"
                                    track-by="id" :custom-label="customLabelExternalOrganization"
                                    placeholder="Optional (if applicable)">
                                </multiselect>
                                <input v-if="editShowOtherField" v-model="editForm.industryPartner" type="text"
                                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                    placeholder="Enter External Organization" />
                            </div>

                            <!-- KPI Pillar Selection -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">KPI Pillar <span
                                        class="text-red-500">*</span></label>
                                <multiselect v-model="editForm.kpiPillarId" :options="formattedKpiPillars" track-by="id"
                                    label="formattedTitle" placeholder="Select KPI Pillar"
                                    @select="onEditKpiPillarSelect" required>
                                </multiselect>
                            </div>

                            <!-- KPI Sub-Section Selection -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">KPI Sub-Section <span
                                        class="text-red-500">*</span></label>
                                <multiselect v-model="editForm.kpiSubSectionId" :options="formattedKpiSubSections"
                                    track-by="id" label="formattedTitle" placeholder="Select KPI Sub-Section"
                                    :disabled="!editForm.kpiPillarId" required>
                                </multiselect>
                            </div>
                        </div>

                        <!-- Faculty-specific fields -->
                        <div v-else class="space-y-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Type of Engagement</label>
                                <select v-model="editForm.engagementType"
                                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none">
                                    <option disabled value="">Select engagement type</option>
                                    <option>Curriculum Development</option>
                                    <option>Research</option>
                                    <option>Guest Lecture</option>
                                    <option>Industry Visit</option>
                                    <option>Project Collaboration</option>
                                    <option>Workshop</option>
                                    <option>Seminar</option>
                                    <option>Panel Discussion</option>
                                    <option>Patent</option>
                                    <option>Other</option>
                                </select>
                                <input v-if="editForm.engagementType == 'Other'" v-model="editOtherEngagementType"
                                    type="text"
                                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                    placeholder="Other Type of Engagement" />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Date of Engagement</label>
                                <date-picker v-model:value="editForm.dateOfEngagement" format="YYYY-MM-DD" type="date"
                                    value-type="format" placeholder="Date of Engagement"></date-picker>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Target Outcome</label>
                                <input v-model="editForm.targetOutcome" type="text"
                                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                    placeholder="e.g. Published paper, internship program" />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">External
                                    Organization</label>
                                <multiselect v-model="editForm.industryPartnerId" :options="currentPartners"
                                    track-by="id" :custom-label="customLabelExternalOrganization"
                                    placeholder="Optional (if applicable)">
                                </multiselect>
                                <input v-if="editShowOtherField" v-model="editForm.industryPartner" type="text"
                                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                    placeholder="Enter External Organization" />
                            </div>

                            <!-- KPI Pillar Selection -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">KPI Pillar <span
                                        class="text-red-500">*</span></label>
                                <multiselect v-model="editForm.kpiPillarId" :options="formattedKpiPillars" track-by="id"
                                    label="formattedTitle" placeholder="Select KPI Pillar"
                                    @select="onEditKpiPillarSelect" required>
                                </multiselect>
                            </div>

                            <!-- KPI Sub-Section Selection -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">KPI Sub-Section <span
                                        class="text-red-500">*</span></label>
                                <multiselect v-model="editForm.kpiSubSectionId" :options="formattedKpiSubSections"
                                    track-by="id" label="formattedTitle" placeholder="Select KPI Sub-Section"
                                    :disabled="!editForm.kpiPillarId" required>
                                </multiselect>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Is this part of a funded
                                    initiative?</label>
                                <div class="flex gap-6 mt-2">
                                    <label class="inline-flex items-center">
                                        <input type="radio" v-model="editForm.funded" :value="true" class="mr-2" />
                                        Yes
                                    </label>
                                    <label class="inline-flex items-center">
                                        <input type="radio" v-model="editForm.funded" :value="false" class="mr-2" />
                                        No
                                    </label>
                                </div>
                            </div>

                            <div v-if="editForm.funded">
                                <label class="block text-sm font-medium text-gray-700 mb-1">Funding Entity</label>
                                <input v-model="editForm.fundingDetails" type="text"
                                    class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                    placeholder="e.g. ADEK Grant 2025" />
                            </div>
                        </div>

                        <!-- Has Documents -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Do you have any documents to
                                upload?</label>
                            <div class="flex gap-6 mt-2">
                                <label class="inline-flex items-center">
                                    <input type="radio" v-model="editForm.hasDocuments" :value="true" class="mr-2" />
                                    Yes
                                </label>
                                <label class="inline-flex items-center">
                                    <input type="radio" v-model="editForm.hasDocuments" :value="false" class="mr-2" />
                                    No
                                </label>
                            </div>

                            <!-- Document Upload Section (same as create form) -->
                            <div v-if="editForm.hasDocuments" class="mt-4 space-y-4">
                                <!-- Existing Attachments Display -->
                                <div
                                    v-if="editingItem && editingItem.attachments && editingItem.attachments.length > 0">
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Current
                                        Documents:</label>
                                    <div class="space-y-2">
                                        <div v-for="(attachment, index) in editingItem.attachments"
                                            :key="'existing-' + index"
                                            class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                                            <i class="fas fa-file-alt text-gray-500"></i>
                                            <input v-model="attachment.file_name" type="text"
                                                class="flex-1 p-2 border border-gray-300 rounded text-sm"
                                                placeholder="Document filename" />
                                            <button type="button" @click="changeAttachmentFile(attachment, index)"
                                                class="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors">
                                                <i class="fas fa-upload mr-1"></i>Change File
                                            </button>
                                            <button type="button" @click="removeAttachment(attachment, index)"
                                                class="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors">
                                                <i class="fas fa-trash mr-1"></i>Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- New Document Upload Section -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Add New
                                        Documents:</label>
                                    <div class="space-y-4">
                                        <div v-for="(doc, index) in editForm.documents" :key="'new-' + index"
                                            class="flex items-center gap-3">
                                            <input v-model="doc.label" type="text" placeholder="Document Label"
                                                class="w-1/2 p-2 border border-gray-300 rounded-xl" />
                                            <input type="file" @change="handleEditFileUpload($event, index)"
                                                class="w-1/2 p-2 border border-gray-300 rounded-xl" />
                                            <button type="button" @click="removeEditDocument(index)"
                                                class="text-red-500 hover:text-red-700 ml-2">Remove</button>
                                        </div>
                                        <button type="button" @click="addEditDocument"
                                            class="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                            <font-awesome-icon :icon="['fas', 'plus']" />
                                            Add Document
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Action Buttons -->
                <div class="flex justify-end gap-4 pt-6 px-8 border-t border-gray-200">
                    <button type="button" @click="closeEditModal"
                        class="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                        Cancel
                    </button>
                    <button type="submit"
                        class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                        Update Engagement
                    </button>
                </div>
            </form>
        </base-modal>

        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>

    </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import ThinkTankView from "./view.vue";
import EngagementsByTypeChart from "./charts/EngagementsByTypeChart.vue";
import EngagementsByEntityChart from "./charts/EngagementsByEntityChart.vue";
import { formattedDate, timeAgo, formatDateToServerFormat } from "../../helpers/utils";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import FCHSLogo from '@/assets/images/dashboard/alumni/FCHS.png';
import ADPOLYLogo from '@/assets/images/dashboard/alumni/ADPOLY.png';
import ADVETILogo from '@/assets/images/dashboard/alumni/ADVETI.png';
import ATSLogo from '@/assets/images/dashboard/alumni/ATS.png';
import AIEHAdvancedSearch from './components/AIEHAdvancedSearch.vue';

const LIST_ACADEMIC_INDUSTRY_ENGAGEMENT_HUB_QUERY = gql`
  query ListAcademicIndustryEngagementHubs($first: Int!, $page: Int!, $entities: [String!], $campuses: [String!], $departments: [String!], $userTypes: [String!], $person: Int, $engagementTypes: [String!], $search: String, $engagement_type: String, $funded: Boolean, $industry_partner: String, $mentor: String, $date_from: Date, $date_to: Date) {
    listAcademicIndustryEngagementHubs(first: $first, page: $page, entities: $entities, campuses: $campuses, departments: $departments, userTypes: $userTypes, person: $person, engagementTypes: $engagementTypes, search: $search, engagement_type: $engagement_type, funded: $funded, industry_partner: $industry_partner, mentor: $mentor, date_from: $date_from, date_to: $date_to) {
      error
      error_code
      message
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
      data {
        id
        user {
            id
            token
            name
            email
            phone_number
            profile_picture_url
            entity
            campuses
            role_name
            role_display_name
            userDepartments {
                department
            }
        }
        title
        description
        engagement_type
        date_of_engagement
        target_outcome
        funded
        funding_details
        expected_outcome
        mentor
        industry_partner
        current_partner_id
        created_at
        created_by_role
        attachments {
          id
          file_path
          file_name
        }
        kpiPillar {
          id
          title
          weight
        }
        kpiSubSection {
          id
          title
          kpi_code
        }
      }
    }
  }
`;

const CREATE_ACADEMIC_INDUSTRY_ENGAGEMENT_HUB_MUTATION = gql`
  mutation CreateAcademicIndustryEngagementHub($input: AcademicIndustryEngagementHubInput!) {
    createAcademicIndustryEngagementHub(input: $input) {
      error
      error_code
      message
    }
  }
`;

const GET_ALL_CURRENT_PARTNERS = gql`
    query GetAllCurrentPartners {
        getAllCurrentPartners {
            error
            error_code
            message
            data {
                id
                name
            }
        }
    }
`;

const GET_ENGAGEMENT_HUB_STATISTICS = gql`
  query GetEngagementHubStatistics($engagement_type: String, $created_by_role: String, $entities: [String!], $campuses: [String!], $departments: [String!], $userTypes: [String!], $person: Int, $engagementTypes: [String!]) {
    engagementHubStatistics(engagement_type: $engagement_type, created_by_role: $created_by_role, entities: $entities, campuses: $campuses, departments: $departments, userTypes: $userTypes, person: $person, engagementTypes: $engagementTypes) {
      error
      error_code
      message
      data {
        totalEngagements
        fundedEngagements
        engagementsByType {
          type
          count
        }
        engagementsByEntity {
          entity
          count
        }
      }
    }
  }
`;

const GET_DEPARTMENTS = gql`
  query GetDepartments($roles: [String!]) {
    academicIndustryEngagementHubUserDepartments(roles: $roles) {
      error
      error_code
      message
      data {
        department
      }
    }
  }
`;

const GET_USER_TYPES = gql`
  query GetUserTypes {
    academicIndustryEngagementHubUserTypes {
      error
      error_code
      message
      data {
        role
        role_to_display
      }
    }
  }
`;

const GET_PERSONS = gql`
  query GetPersons($roles: [String!], $departments: [String!]) {
    academicIndustryEngagementHubPersons(roles: $roles, departments: $departments) {
      error
      error_code
      message
      data {
        user_id
        name
      }
    }
  }
`;

const GET_ENGAGEMENT_TYPES = gql`
  query GetEngagementTypes($userTypes: [String!], $departments: [String!], $person: Int) {
    academicIndustryEngagementHubEngagementTypes(userTypes: $userTypes, departments: $departments, person: $person) {
      error
      error_code
      message
      data {
        engagement_type
      }
    }
  }
`;

const GET_KPI_PILLARS = gql`
  query GetKpiPillars {
    kpiPillars {
      error
      error_code
      message
      data {
        id
        title
      }
    }
  }
`;

const GET_KPI_SUB_SECTIONS_BY_PILLAR = gql`
  query GetKpiSubSectionsByPillar($pillar_id: Int!) {
    kpiSubSectionsByPillar(pillar_id: $pillar_id) {
      error
      error_code
      message
      data {
        id
        title
        kpi_code
      }
    }
  }
`;

const UPDATE_ACADEMIC_INDUSTRY_ENGAGEMENT_HUB_MUTATION = gql`
  mutation UpdateAcademicIndustryEngagementHub($input: UpdateAcademicIndustryEngagementHubInput!) {
    updateAcademicIndustryEngagementHub(input: $input) {
      error
      error_code
      message
    }
  }
`;

export default {
    name: "academic-industry-engagement-hub",
    components: {
        Pagination,
        ThinkTankView,
        EngagementsByTypeChart,
        EngagementsByEntityChart,
        Multiselect,
        AIEHAdvancedSearch,
    },
    data() {
        return {
            showFilter: false,
            basicSearchText: "",
            advancedSearch: {
                engagement_type: "",
                funded: null,
                industry_partner: "",
                mentor: "",
                date_from: "",
                date_to: "",
            },
            filters: {
                entities: [],
                campuses: [],
                departments: [],
                userTypes: [],
                person: null,
                engagementTypes: [],
            },
            entityOptions: [
                { id: 'FCHS', name: 'FCHS' },
                { id: 'ADPOLY', name: 'ADPOLY' },
                { id: 'ATHS', name: 'ATS' },
                { id: 'ADVETI', name: 'ADVETI' },
            ],
            campusOptions: [],
            departmentOptions: [],
            userTypeOptions: [],
            personOptions: [],
            engagementTypeOptions: [],
            kpiPillars: [],
            kpiSubSections: [],
            first: 6,
            page: 1,
            listAcademicIndustryEngagementHubs: {
                data: [],
                paginatorInfo: {
                    total: 0,
                    count: 0,
                    perPage: 6,
                    currentPage: 1,
                    lastPage: 1,
                    hasMorePages: false,
                    firstItem: null,
                    lastItem: null,
                }
            },
            isLoading: false,
            showForm: false,
            form: {
                title: "",
                description: "",
                hasDocuments: false,
                documents: [],
                // Faculty
                engagementType: "",
                dateOfEngagement: null,
                targetOutcome: "",
                funded: false,
                fundingDetails: "",
                // Student
                expectedOutcome: "",
                mentor: "",
                industryPartnerId: null,
                industryPartner: "",
                kpiPillarId: null,
                kpiSubSectionId: null,
            },
            otherEngagementType: "",
            isViewItem: false,
            selectedItem: null,
            currentPartners: [],
            engagementHubStatistics: {
                totalEngagements: 0,
                fundedEngagements: 0,
                engagementsByType: [],
                engagementsByEntity: [],
            },
            settings: [],
            currentDateTime: this.getFormatDateToServerFormat(new Date()),
            timeUpdater: null,
            // Edit functionality
            showEditModal: false,
            editingItem: null,
            editForm: {
                title: "",
                description: "",
                expectedOutcome: "",
                mentor: "",
                industryPartnerId: null,
                industryPartner: "",
                engagementType: "",
                dateOfEngagement: "",
                targetOutcome: "",
                fundingDetails: "",
                funded: false,
                hasDocuments: false,
                documents: [],
                kpiPillarId: null,
                kpiSubSectionId: null,
            },
            editOtherEngagementType: "",
        };
    },
    created() {
        this.loadData();
        this.getAllCurrentPartners();
        this.fetchEngagementHubStatistics();
        this.fetchCampuses();
        this.fetchDepartments();
        this.fetchUserTypes();
        this.fetchPersons();
        this.fetchEngagementTypes();
        this.fetchKpiPillars();
    },
    watch: {
        settings(val) {
            if (val) {
                this.$store.commit("settings/setSettings", val);

                let current = null;

                if (Array.isArray(val)) {
                    const found = val.find(setting => setting.name === "current_datetime");
                    if (found && found.value) {
                        current = found.value;
                    }
                }

                if (!current) {
                    const now = new Date();
                    current = this.getFormatDateToServerFormat(now);
                }

                this.currentDateTime = current;

                // Start the updater only after currentDateTime is set
                this.startCurrentDateTimeUpdater();
            }
        }
        ,
        'filters.entities': {
            handler() {
                // Reset campuses if they are not in the filtered list anymore
                const allowed = this.campusOptionsFiltered.map(c => c.id);
                this.filters.campuses = (this.filters.campuses || []).filter(c => allowed.indexOf(c.id) !== -1);
            },
            deep: true
        },
        'filters.userTypes': {
            handler() {
                // Clear departments, person, and engagement types when user types change
                this.filters.departments = [];
                this.filters.person = null;
                this.filters.engagementTypes = [];
                // Refetch departments, persons, and engagement types based on selected user types
                this.fetchDepartments();
                this.fetchPersons();
                this.fetchEngagementTypes();
            },
            deep: true
        },
        'filters.departments': {
            handler() {
                // Clear person and engagement types when departments change
                this.filters.person = null;
                this.filters.engagementTypes = [];
                // Refetch persons and engagement types based on selected departments
                this.fetchPersons();
                this.fetchEngagementTypes();
            },
            deep: true
        },
        'filters.person': {
            handler() {
                // Clear engagement types when person changes
                this.filters.engagementTypes = [];
                // Refetch engagement types based on selected person
                this.fetchEngagementTypes();
            },
            deep: true
        }
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        currentUserId() {
            return this.$store.getters.userId;
        },
        isSuperAdmin() {
            return this.$store.getters.isSuperAdmin;
        },
        items() {
            return this.listAcademicIndustryEngagementHubs.data;
        },
        hasItems() {
            if (this.items && this.items.length > 0) {
                return true;
            } else {
                return false;
            }
        },
        paginatorInfo() {
            return this.listAcademicIndustryEngagementHubs.paginatorInfo;
        },
        showOtherField() {
            return this.form.industryPartnerId && this.form.industryPartnerId.id === 0;
        },
        campusOptionsFiltered() {
            // Filter campuses by selected entities and portal_campus presence
            const selectedEntities = (this.filters.entities || []).map(e => e.id);
            const byEntity = selectedEntities.length
                ? this.campusOptions.filter(c => selectedEntities.indexOf(c.entity) !== -1)
                : this.campusOptions;
            return byEntity
                .filter(c => c.name && c.name !== '')
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        formattedKpiPillars() {
            return this.kpiPillars.map((pillar) => ({
                ...pillar,
                formattedTitle: `${pillar.id}. ${pillar.title}`
            }));
        },
        formattedKpiSubSections() {
            return this.kpiSubSections.map((subSection) => ({
                ...subSection,
                formattedTitle: `${subSection.kpi_code} ${subSection.title}`
            }));
        },
        canEditItem() {
            return (item) => {
                const canEdit = this.isSuperAdmin || (item.user && item.user.id === this.currentUserId);
                return canEdit;
            };
        },
        editShowOtherField() {
            return this.editForm.industryPartnerId && this.editForm.industryPartnerId.id === 0;
        },
    },
    apollo: {
        settings() {
            return {
                query: gql`query GetSettings {
                    settings {
                        name
                        value
                    }
                }`,
                fetchPolicy: 'cache-and-network'
            }
        },
    },
    methods: {
        async loadData() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: LIST_ACADEMIC_INDUSTRY_ENGAGEMENT_HUB_QUERY,
                    variables: {
                        first: this.first,
                        page: this.page,
                        entities: (this.filters.entities || []).map(e => e.id),
                        campuses: (this.filters.campuses || []).map(c => c.id),
                        departments: (this.filters.departments || []).map(d => d.id),
                        userTypes: (this.filters.userTypes || []).map(u => u.id),
                        person: this.filters.person ? this.filters.person.id : null,
                        engagementTypes: (this.filters.engagementTypes || []).map(e => e.id),
                        search: this.basicSearchText || null,
                        engagement_type: this.advancedSearch.engagement_type || null,
                        funded: this.advancedSearch.funded,
                        industry_partner: this.advancedSearch.industry_partner || null,
                        mentor: this.advancedSearch.mentor || null,
                        date_from: this.advancedSearch.date_from || null,
                        date_to: this.advancedSearch.date_to || null,
                    },
                    fetchPolicy: 'network-only'
                });

                this.listAcademicIndustryEngagementHubs = response.data.listAcademicIndustryEngagementHubs;

            } catch (error) {
                console.error("Failed to load academic industry engagement hub:", error);
            }
            this.isLoading = false;
        },
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        clearFilters() {
            this.showFilter = false;
            this.filters = {
                entities: [],
                campuses: [],
                departments: [],
                userTypes: [],
                person: null,
                engagementTypes: [],
            };
            this.basicSearchText = "";
            this.advancedSearch = {
                engagement_type: "",
                funded: null,
                industry_partner: "",
                mentor: "",
                date_from: "",
                date_to: "",
            };
            this.loadData();
            this.fetchEngagementHubStatistics();
        },
        clearSearch() {
            this.basicSearchText = "";
            this.advancedSearch = {
                engagement_type: "",
                funded: null,
                industry_partner: "",
                mentor: "",
                date_from: "",
                date_to: "",
            };
            this.setPage(1);
        },
        applyFilters() {
            this.toggleFilter();
            this.loadData();
            this.fetchEngagementHubStatistics();
        },
        onBasicSearch(text) {
            this.basicSearchText = text;
            this.setPage(1);
        },
        onAdvancedSearch(filters) {
            this.advancedSearch = { ...this.advancedSearch, ...filters };
            this.setPage(1);
        },
        setPage(page) {
            this.page = page;
            this.loadData();
        },
        addDocument() {
            this.form.documents.push({ label: '', file: null });
        },
        handleFileUpload(event, index) {
            const file = event.target.files[0];
            if (file) {
                this.form.documents[index].file = file;
            }
        },
        removeDocument(index) {
            this.form.documents.splice(index, 1);
        },
        async submitForm() {
            // Validate KPI fields
            if (!this.form.kpiPillarId) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please select a KPI Pillar',
                });
                return;
            }

            if (!this.form.kpiSubSectionId) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please select a KPI Sub-Section',
                });
                return;
            }

            this.form.documents = this.form.documents.filter(doc => {
                return doc.label && doc.file;
            });

            if (this.form.engagementType === 'Other') {
                this.form.engagementType = this.otherEngagementType;
            }

            const newForm = { ...this.form };

            if (this.form.industryPartnerId) {
                newForm.industryPartnerId = parseInt(this.form.industryPartnerId.id);
                if (newForm.industryPartnerId != 0) {
                    newForm.industryPartner = this.form.industryPartnerId.name;
                }
            } else {
                newForm.industryPartnerId = null;
                newForm.industryPartner = "";
            }

            // Handle KPI fields
            if (this.form.kpiPillarId) {
                newForm.kpiPillarId = parseInt(this.form.kpiPillarId.id);
            } else {
                newForm.kpiPillarId = null;
            }

            if (this.form.kpiSubSectionId) {
                newForm.kpiSubSectionId = parseInt(this.form.kpiSubSectionId.id);
            } else {
                newForm.kpiSubSectionId = null;
            }

            this.isLoading = true;

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: CREATE_ACADEMIC_INDUSTRY_ENGAGEMENT_HUB_MUTATION,
                    variables: {
                        input: newForm
                    },
                });

                const response = data.createAcademicIndustryEngagementHub;

                if (response.error) {
                    return this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: response.message,
                    });
                }

                this.$swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: response.message,
                    showConfirmButton: false,
                    timer: 1000,
                    timerProgressBar: true,
                });

                this.loadData();
                this.fetchEngagementHubStatistics();
                this.fetchDepartments();
                this.fetchUserTypes();
                this.fetchPersons();
                this.fetchEngagementTypes();

            } catch (error) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: error.message || 'Could not create Engagement Proposal.',
                });
            } finally {
                this.isLoading = false;
            }

            this.resetForm();
            this.showForm = false;
        },
        cancelSubmit() {
            this.showForm = false;
            this.resetForm();
        },
        resetForm() {
            this.form = {
                title: "",
                description: "",
                hasDocuments: false,
                documents: [],
                engagementType: "",
                dateOfEngagement: null,
                targetOutcome: "",
                funded: false,
                fundingDetails: "",
                expectedOutcome: "",
                mentor: "",
                industryPartnerId: null,
                industryPartner: "",
                kpiPillarId: null,
                kpiSubSectionId: null,
            };
            this.otherEngagementType = "";
            this.kpiSubSections = []; // Reset sub-sections when form is reset
        },
        getUrl(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        viewItem(item) {
            this.selectedItem = item;
            this.isViewItem = true;
        },
        closeModal() {
            this.isViewItem = false;
            this.selectedItem = null;
        },
        getFormattedDate(date) {
            return formattedDate(date);
        },
        async getAllCurrentPartners() {
            this.isLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_ALL_CURRENT_PARTNERS
                });

                const { error, message, data: currentPartners } = data.getAllCurrentPartners;

                if (error) {
                    this.$swal.fire('Error', message, 'error');
                    return;
                }

                this.currentPartners = [...currentPartners, { id: 0, name: 'Other' }];

            } catch (err) {
                console.error("Failed to fetch all current partners:", err);
            } finally {
                this.isLoading = false;
            }
        },
        async fetchCampuses() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`query GetCampusesPublic {
                        campusesPublic {
                            error
                            message
                            data {
                                Entity
                                portal_campus
                            }
                        }
                    }`,
                    fetchPolicy: 'network-only'
                });

                if (data && data.campusesPublic && data.campusesPublic.data) {
                    const rows = data.campusesPublic.data;
                    // Build options with non-null portal_campus only
                    this.campusOptions = rows
                        .filter(r => r && r.portal_campus)
                        .map(r => ({ id: r.portal_campus, name: r.portal_campus, entity: (r.Entity || '').trim() }));
                }
            } catch (error) {
                console.error('Failed to fetch campuses:', error);
            }
        },
        async fetchDepartments() {
            try {
                const selectedRoles = (this.filters.userTypes || []).map(u => u.id);
                const { data } = await this.$apollo.query({
                    query: GET_DEPARTMENTS,
                    variables: {
                        roles: selectedRoles.length > 0 ? selectedRoles : null,
                    },
                    fetchPolicy: 'network-only'
                });

                if (data && data.academicIndustryEngagementHubUserDepartments && data.academicIndustryEngagementHubUserDepartments.data) {
                    this.departmentOptions = data.academicIndustryEngagementHubUserDepartments.data.map(dept => ({
                        id: dept.department,
                        name: dept.department
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch departments:', error);
            }
        },
        async fetchUserTypes() {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_USER_TYPES,
                    fetchPolicy: 'network-only'
                });

                if (data && data.academicIndustryEngagementHubUserTypes && data.academicIndustryEngagementHubUserTypes.data) {
                    this.userTypeOptions = data.academicIndustryEngagementHubUserTypes.data.map(userType => ({
                        id: userType.role,
                        name: userType.role_to_display
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch user types:', error);
            }
        },
        async fetchPersons() {
            try {
                const selectedRoles = (this.filters.userTypes || []).map(u => u.id);
                const selectedDepartments = (this.filters.departments || []).map(d => d.id);
                const { data } = await this.$apollo.query({
                    query: GET_PERSONS,
                    variables: {
                        roles: selectedRoles.length > 0 ? selectedRoles : null,
                        departments: selectedDepartments.length > 0 ? selectedDepartments : null,
                    },
                    fetchPolicy: 'network-only'
                });

                if (data && data.academicIndustryEngagementHubPersons && data.academicIndustryEngagementHubPersons.data) {
                    this.personOptions = data.academicIndustryEngagementHubPersons.data.map(person => ({
                        id: person.user_id,
                        name: person.name
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch persons:', error);
            }
        },
        async fetchEngagementTypes() {
            try {
                const selectedUserTypes = (this.filters.userTypes || []).map(u => u.id);
                const selectedDepartments = (this.filters.departments || []).map(d => d.id);
                const selectedPerson = this.filters.person ? this.filters.person.id : null;

                const { data } = await this.$apollo.query({
                    query: GET_ENGAGEMENT_TYPES,
                    variables: {
                        userTypes: selectedUserTypes.length > 0 ? selectedUserTypes : null,
                        departments: selectedDepartments.length > 0 ? selectedDepartments : null,
                        person: selectedPerson,
                    },
                    fetchPolicy: 'network-only'
                });

                if (data && data.academicIndustryEngagementHubEngagementTypes && data.academicIndustryEngagementHubEngagementTypes.data) {
                    this.engagementTypeOptions = data.academicIndustryEngagementHubEngagementTypes.data.map(engagementType => ({
                        id: engagementType.engagement_type,
                        name: engagementType.engagement_type
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch engagement types:', error);
            }
        },
        customLabelExternalOrganization(option) {
            return option.name;
        },
        async fetchEngagementHubStatistics() {
            try {
                const response = await this.$apollo.query({
                    query: GET_ENGAGEMENT_HUB_STATISTICS,
                    variables: {
                        entities: (this.filters.entities || []).map(e => e.id),
                        campuses: (this.filters.campuses || []).map(c => c.id),
                        departments: (this.filters.departments || []).map(d => d.id),
                        userTypes: (this.filters.userTypes || []).map(u => u.id),
                        person: this.filters.person ? this.filters.person.id : null,
                        engagementTypes: (this.filters.engagementTypes || []).map(e => e.id),
                    },
                    fetchPolicy: "network-only",
                });

                if (response.data.engagementHubStatistics.error) {
                    console.error("GraphQL Error:", response.data.engagementHubStatistics.message);
                    return;
                }

                this.engagementHubStatistics = response.data.engagementHubStatistics.data;
            } catch (error) {
                console.error("Failed to load engagement hub statistics:", error);
            }
        },
        formatNumber(num) {
            return num.toLocaleString();
        },
        getTopEngagementType() {
            if (this.engagementHubStatistics.engagementsByType && this.engagementHubStatistics.engagementsByType.length > 0) {
                return this.engagementHubStatistics.engagementsByType[0].type;
            }
            return "N/A";
        },
        getTopEngagementTypeCount() {
            if (this.engagementHubStatistics.engagementsByType && this.engagementHubStatistics.engagementsByType.length > 0) {
                return this.engagementHubStatistics.engagementsByType[0].count;
            }
            return 0;
        },
        getFundedPercentage() {
            const fundedCount = this.engagementHubStatistics.fundedEngagements;
            const total = this.engagementHubStatistics.totalEngagements;
            if (total > 0) {
                return ((fundedCount / total) * 100).toFixed(1);
            }
            return "0.0";
        },
        onTypeChartClick(type) {
            // You can implement filtering by engagement type here if needed
            console.log('Type clicked:', type);
        },
        onEntityChartClick(entity) {
            // You can implement filtering by entity here if needed
            console.log('Entity clicked:', entity);
        },
        profilePicture(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        postedAt(createdAt) {
            return timeAgo(createdAt, this.currentDateTime);
        },
        getFormatDateToServerFormat(date) {
            return formatDateToServerFormat(date);
        },
        startCurrentDateTimeUpdater() {
            if (this.timeUpdater) clearInterval(this.timeUpdater); // Clear old interval if exists

            this.timeUpdater = setInterval(() => {
                const updated = new Date(this.currentDateTime);
                updated.setMinutes(updated.getMinutes() + 1);
                this.currentDateTime = this.getFormatDateToServerFormat(updated);
            }, 60000); // Every 60 seconds
        },
        entityLogo(entity) {
            const logos = {
                FCHS: FCHSLogo,
                ADPOLY: ADPOLYLogo,
                ADVETI: ADVETILogo,
                ATS: ATSLogo,
                ATHS: ATSLogo,
            };
            return logos[entity.split(',')[0]] || '';
        },
        async fetchKpiPillars() {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_KPI_PILLARS,
                    fetchPolicy: 'network-only'
                });

                if (data && data.kpiPillars && data.kpiPillars.data) {
                    this.kpiPillars = data.kpiPillars.data;
                }
            } catch (error) {
                console.error('Failed to fetch KPI pillars:', error);
            }
        },
        async onKpiPillarSelect(selectedPillar) {
            if (selectedPillar) {
                this.form.kpiSubSectionId = null; // Reset sub-section selection
                await this.fetchKpiSubSections(selectedPillar.id);
            } else {
                this.kpiSubSections = [];
                this.form.kpiSubSectionId = null;
            }
        },
        async fetchKpiSubSections(pillarId) {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_KPI_SUB_SECTIONS_BY_PILLAR,
                    variables: {
                        pillar_id: parseInt(pillarId)
                    },
                    fetchPolicy: 'network-only'
                });

                if (data && data.kpiSubSectionsByPillar && data.kpiSubSectionsByPillar.data) {
                    this.kpiSubSections = data.kpiSubSectionsByPillar.data;
                }
            } catch (error) {
                console.error('Failed to fetch KPI sub-sections:', error);
            }
        },
        // Edit functionality methods
        async editItem(item) {
            // Create a deep copy of the item to make it extensible
            this.editingItem = JSON.parse(JSON.stringify(item));

            // Store original attachments for comparison
            if (item.attachments) {
                this.editingItem.originalAttachments = JSON.parse(JSON.stringify(item.attachments));
            }

            // Ensure all required data is loaded
            if (this.kpiPillars.length === 0) {
                await this.fetchKpiPillars();
            }
            if (this.currentPartners.length === 0) {
                await this.getAllCurrentPartners();
            }

            await this.populateEditForm(this.editingItem);
            this.showEditModal = true;
        },
        async populateEditForm(item) {
            // Set basic form data first
            this.editForm = {
                title: item.title || "",
                description: item.description || "",
                expectedOutcome: item.expected_outcome || "",
                mentor: item.mentor || "",
                industryPartnerId: null, // Will be set after partners are loaded
                industryPartner: item.industry_partner || "",
                engagementType: item.engagement_type || "",
                dateOfEngagement: item.date_of_engagement || "",
                targetOutcome: item.target_outcome || "",
                fundingDetails: item.funding_details || "",
                funded: item.funded || false,
                hasDocuments: (item.attachments && item.attachments.length > 0) || item.has_documents || false,
                documents: [], // Initialize empty documents array for new uploads
                kpiPillarId: null, // Will be set after pillars are loaded
                kpiSubSectionId: null, // Will be set after sub-sections are loaded
            };

            // Set other engagement type if applicable
            const engagementTypeOptions = [
                'Curriculum Development', 'Research', 'Guest Lecture', 'Industry Visit',
                'Project Collaboration', 'Workshop', 'Seminar', 'Panel Discussion', 'Patent'
            ];
            if (item.engagement_type && !engagementTypeOptions.includes(item.engagement_type)) {
                this.editOtherEngagementType = item.engagement_type;
                this.editForm.engagementType = "Other";
            }

            // Set industry partner if current_partner_id exists
            if (item.current_partner_id && this.currentPartners.length > 0) {
                this.editForm.industryPartnerId = this.currentPartners.find(p => p.id == item.current_partner_id) || null;
            } else if (item.industry_partner && this.currentPartners.length > 0) {
                // Check if it's a custom "Other" organization
                const foundPartner = this.currentPartners.find(p => p.name === item.industry_partner);
                if (foundPartner) {
                    // Found in the list, use it
                    this.editForm.industryPartnerId = foundPartner;
                } else {
                    // Not found in list, it's a custom "Other" organization
                    this.editForm.industryPartnerId = { id: 0, name: "Other" };
                    this.editForm.industryPartner = item.industry_partner; // Set the custom name
                }
            }

            // Set KPI Pillar
            if (item.kpiPillar && this.kpiPillars.length > 0) {
                const foundPillar = this.kpiPillars.find(p => p.id == item.kpiPillar.id);
                if (foundPillar) {
                    // Set the complete object in the same format as the multiselect expects
                    this.editForm.kpiPillarId = {
                        __typename: foundPillar.__typename,
                        id: foundPillar.id,
                        title: foundPillar.title,
                        formattedTitle: foundPillar.formattedTitle || `${foundPillar.id}. ${foundPillar.title}`
                    };
                }

                // Load KPI sub-sections and then set the sub-section
                if (item.kpiPillar.id) {
                    await this.fetchKpiSubSections(item.kpiPillar.id);
                    // Set KPI Sub-Section after sub-sections are loaded
                    if (item.kpiSubSection && this.kpiSubSections.length > 0) {
                        const foundSubSection = this.kpiSubSections.find(s => s.id == item.kpiSubSection.id);
                        if (foundSubSection) {
                            // Set the complete object in the same format as the multiselect expects
                            this.editForm.kpiSubSectionId = {
                                __typename: foundSubSection.__typename,
                                id: foundSubSection.id,
                                title: foundSubSection.title,
                                kpi_code: foundSubSection.kpi_code,
                                formattedTitle: foundSubSection.formattedTitle || `${foundSubSection.kpi_code} - ${foundSubSection.title}`
                            };
                        }
                    }
                }
            }
        },
        async onEditKpiPillarSelect(selectedPillar) {
            if (selectedPillar) {
                this.editForm.kpiSubSectionId = null;
                await this.fetchKpiSubSections(selectedPillar.id);
            } else {
                this.kpiSubSections = [];
                this.editForm.kpiSubSectionId = null;
            }
        },
        async updateEngagement() {
            try {
                // Validate required fields
                if (!this.editForm.title || !this.editForm.description ||
                    !this.editForm.kpiPillarId || !this.editForm.kpiSubSectionId) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Validation Error',
                        text: 'Please fill in all required fields.',
                    });
                    return;
                }

                // Validate editing item ID
                if (!this.editingItem || !this.editingItem.id) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Validation Error',
                        text: 'Invalid engagement item. Please try again.',
                    });
                    return;
                }

                // Prepare the update data based on role
                const updateData = {
                    id: parseInt(this.editingItem.id),
                    title: this.editForm.title,
                    description: this.editForm.description,
                    kpiPillarId: parseInt(this.editForm.kpiPillarId.id),
                    kpiSubSectionId: parseInt(this.editForm.kpiSubSectionId.id),
                };

                // Add role-specific fields
                if (this.editingItem.user.role_name === 'user') {
                    // Student fields
                    updateData.expectedOutcome = this.editForm.expectedOutcome || null;
                    updateData.mentor = this.editForm.mentor || null;
                    updateData.industryPartner = this.editForm.industryPartnerId && this.editForm.industryPartnerId.id !== 0
                        ? this.editForm.industryPartnerId.name
                        : (this.editForm.industryPartner || null);
                    updateData.hasDocuments = this.editForm.hasDocuments;
                } else {
                    // Faculty/staff fields
                    updateData.industryPartner = this.editForm.industryPartnerId && this.editForm.industryPartnerId.id !== 0
                        ? this.editForm.industryPartnerId.name
                        : (this.editForm.industryPartner || null);
                    updateData.engagementType = this.editForm.engagementType === 'Other'
                        ? this.editOtherEngagementType
                        : (this.editForm.engagementType || null);
                    updateData.dateOfEngagement = this.editForm.dateOfEngagement || null;
                    updateData.targetOutcome = this.editForm.targetOutcome || null;
                    updateData.funded = this.editForm.funded;
                    updateData.hasDocuments = this.editForm.hasDocuments;
                    // If not funded, set funding details to null
                    updateData.fundingDetails = this.editForm.funded ? (this.editForm.fundingDetails || null) : null;
                }

                // Handle attachments for all roles
                {
                    // Handle document deletion if hasDocuments is false
                    if (!this.editForm.hasDocuments && this.editingItem && this.editingItem.attachments && this.editingItem.attachments.length > 0) {
                        // Add flag to indicate attachments should be deleted
                        updateData.deleteAttachments = true;
                    }

                    // Handle individual attachment removal
                    if (this.editingItem.removedAttachments && this.editingItem.removedAttachments.length > 0) {
                        updateData.removeAttachments = this.editingItem.removedAttachments;
                    }

                    // Handle new document uploads
                    const newDocuments = this.editForm.documents.filter(doc => doc.file && doc.label);
                    if (newDocuments.length > 0) {
                        updateData.newDocuments = newDocuments.map(doc => ({
                            label: doc.label,
                            file: doc.file
                        }));
                    }

                    // Handle file changes for existing attachments
                    if (this.editingItem.fileChanges && this.editingItem.fileChanges.length > 0) {
                        updateData.fileChanges = this.editingItem.fileChanges.map(change => ({
                            attachmentId: change.attachmentId,
                            newFile: change.newFile,
                            newFileName: change.newFileName
                        }));
                    }

                    // Handle filename changes for existing attachments (without file change)
                    const filenameChanges = [];
                    if (this.editingItem && this.editingItem.attachments) {
                        this.editingItem.attachments.forEach((attachment, index) => {
                            // Check if filename was changed (compare with original data)
                            const originalAttachment = this.editingItem.originalAttachments && this.editingItem.originalAttachments[index];
                            if (originalAttachment && attachment.file_name !== originalAttachment.file_name) {
                                filenameChanges.push({
                                    attachmentId: attachment.id || originalAttachment.id,
                                    newFileName: attachment.file_name
                                });
                            }
                        });
                    }
                    if (filenameChanges.length > 0) {
                        updateData.filenameChanges = filenameChanges;
                    }
                }

                // Call the update mutation
                const { data } = await this.$apollo.mutate({
                    mutation: UPDATE_ACADEMIC_INDUSTRY_ENGAGEMENT_HUB_MUTATION,
                    variables: {
                        input: updateData
                    }
                });

                if (data && data.updateAcademicIndustryEngagementHub && !data.updateAcademicIndustryEngagementHub.error) {
                    this.$swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: 'Engagement updated successfully.',
                    });

                    this.closeEditModal();
                    this.loadData(); // Refresh the list
                } else {
                    throw new Error((data && data.updateAcademicIndustryEngagementHub && data.updateAcademicIndustryEngagementHub.message) || 'Update failed');
                }
            } catch (error) {
                console.error('Failed to update engagement:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Update Failed',
                    text: error.message || 'Failed to update engagement. Please try again.',
                });
            }
        },
        closeEditModal() {
            this.showEditModal = false;
            this.editingItem = null;
            this.resetEditForm();
        },
        resetEditForm() {
            this.editForm = {
                title: "",
                description: "",
                expectedOutcome: "",
                mentor: "",
                industryPartnerId: null,
                industryPartner: "",
                engagementType: "",
                dateOfEngagement: "",
                targetOutcome: "",
                fundingDetails: "",
                funded: false,
                hasDocuments: false,
                documents: [],
                kpiPillarId: null,
                kpiSubSectionId: null,
            };
            this.editOtherEngagementType = "";
        },
        // Attachment management methods
        changeAttachmentFile(attachment, index) {
            // Create a file input element
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif';
            input.multiple = false;

            input.onchange = (event) => {
                const file = event.target.files[0];
                if (file) {
                    // Store the new file and attachment info for update
                    if (!this.editingItem.fileChanges) {
                        this.editingItem.fileChanges = [];
                    }

                    this.editingItem.fileChanges.push({
                        attachmentId: attachment.id || index,
                        oldFileName: attachment.file_name,
                        newFile: file,
                        newFileName: file.name
                    });

                    // Update the display name immediately
                    attachment.file_name = file.name;

                    this.$swal.fire({
                        title: 'File Selected',
                        text: `File "${file.name}" will replace "${attachment.oldFileName || attachment.file_name}" when you save the changes.`,
                        icon: 'success',
                        timer: 3000
                    });
                }
            };

            input.click();
        },
        removeAttachment(attachment, index) {
            this.$swal.fire({
                title: 'Remove Attachment',
                text: `Are you sure you want to remove "${attachment.file_name}"?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, remove it!',
                cancelButtonText: 'Cancel'
            }).then((result) => {
                if (result.isConfirmed) {
                    // Initialize removedAttachments array if it doesn't exist
                    if (!this.editingItem.removedAttachments) {
                        this.editingItem.removedAttachments = [];
                    }

                    // Add the attachment ID to the removed list
                    if (attachment.id) {
                        this.editingItem.removedAttachments.push(attachment.id);
                    }

                    // Show success message
                    this.$swal.fire({
                        title: 'Removed!',
                        text: `"${attachment.file_name}" has been removed.`,
                        icon: 'success',
                        timer: 2000
                    });

                    // Remove from the local array
                    if (this.editingItem && this.editingItem.attachments) {
                        this.editingItem.attachments.splice(index, 1);

                        // If no attachments left, set hasDocuments to false
                        if (this.editingItem.attachments.length === 0) {
                            this.editForm.hasDocuments = false;
                        }
                    }
                }
            });
        },
        // Edit document management methods (same as create form)
        addEditDocument() {
            this.editForm.documents.push({ label: '', file: null });
        },
        handleEditFileUpload(event, index) {
            const file = event.target.files[0];
            if (file) {
                this.editForm.documents[index].file = file;
            }
        },
        removeEditDocument(index) {
            this.editForm.documents.splice(index, 1);
        },
        goToProfile(token) {
            this.$router.push(`/profile/${token}`);
        },
    },
    beforeUnmount() {
        if (this.timeUpdater) clearInterval(this.timeUpdater);
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}
</style>
